// @ts-nocheck
// Agent Portal (/cti2) — call-control handlers, split out of
// AgentPortalPage.tsx. Byte-faithful port from CTIAgentUI.tsx. Owns
// hold/mute/transfer-picker state itself (nothing outside this hook reads
// it except the Dialer/TransferConferencePicker props AgentPortalPage
// wires up), takes `setManualScreen` as a param since that state is shared
// with the screen derivation elsewhere.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCallStore, resolveVoicemailChannels } from '@/router/zustand/callStore';
import { isMobileProfileActive, getActiveTelephonyProfile } from '@/utilities/mobileCallEvents';
import { postApiCall } from '@/service/httpCall';
import {
  API_AGENT_CALL_HOLD,
  API_AGENT_CALL_UNHOLD,
  API_AGENT_CALL_MUTE,
  API_AGENT_CALL_UNMUTE,
  API_AGENT_MOBILE_HANGUP,
  API_AGENT_VOICEMAIL,
} from '@/service/url';
import { trackActivity } from '@/service/activityTracker';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Storage } from '@/utilities/setting';
import { toast } from 'sonner';

export function useCallActions({ callState, userData, setManualScreen, isOutboundEnabled = true }) {
  const [holdOn, setHoldOn] = useState(false);
  const [muteOn, setMuteOn] = useState(false);
  const [holdLoading, setHoldLoading] = useState(false);
  const [muteLoading, setMuteLoading] = useState(false);
  const [pickerMode, setPickerMode] = useState(null);
  const [voicemailSending, setVoicemailSending] = useState(false);
  const voicemailInFlightRef = useRef(false);

  // Neither flag ever reset on its own — muting/holding a call, then
  // ending it without un-muting/un-holding first, left the next call
  // starting with the dialer still showing mute/hold as active.
  useEffect(() => {
    if (callState === 'idle') { setMuteOn(false); setHoldOn(false); }
  }, [callState]);

  const handleToggleHold = useCallback(async () => {
    if (holdLoading) return;
    const state = useCallStore.getState();
    const isMobile = isMobileProfileActive();
    const sipHdrs = state.sipHeaders || {};
    const sess = state.session;
    const getCallId = () => sipHdrs?.['call-id'] || (() => { try { return sess?.request?.getHeader?.('Call-ID') || null; } catch { return null; } })();
    const getChannelId = () => state.channelId || sipHdrs?.['x-ns-channelid'] || (() => { try { return sess?.request?.getHeader?.('X-NS-ChannelId') || null; } catch { return null; } })();
    const channelId = isMobile ? state.bPartyChannelId : getChannelId();
    const callId = isMobile ? state.bPartyChannelId : getCallId();
    if (!channelId || !callId) { toast.error('Channel ID or Call ID not found for active call'); return; }
    const goingToHold = !holdOn;
    const url = (goingToHold ? API_AGENT_CALL_HOLD : API_AGENT_CALL_UNHOLD).replace(':id', encodeURIComponent(String(channelId)));
    setHoldLoading(true);
    try {
      await postApiCall(url, { call_id: callId, activeCli: state.activeCliNumber || null, from: state.callerNumber || null, pbx_name: state.pbxName || null });
      setHoldOn((v) => !v);
      trackActivity(goingToHold ? 'call_hold' : 'call_unhold', { call_id: callId });
    } catch (err: any) {
      toast.error(friendlyApiError(err) || `Failed to ${goingToHold ? 'hold' : 'unhold'} call`);
    } finally { setHoldLoading(false); }
  }, [holdLoading, holdOn]);

  const handleToggleMute = useCallback(async () => {
    if (muteLoading) return;
    if (!isMobileProfileActive()) {
      setMuteOn((value) => {
        const next = !value;
        if (next) {
          useCallStore.getState().startWebrtcMute();
        } else {
          useCallStore.getState().stopWebrtcMute();
        }
        trackActivity(next ? 'mute' : 'unmute', { call_id: useCallStore.getState().sipHeaders?.['call-id'] || null });
        return next;
      });
      return;
    }
    const profile: any = getActiveTelephonyProfile();
    const muteTargetId = profile?.number?.number || null;
    if (!muteTargetId || !userData?.agentData?.id) { toast.error('Agent extension/number not found for mute'); return; }
    const goingToMute = !muteOn;
    const url = (goingToMute ? API_AGENT_CALL_MUTE : API_AGENT_CALL_UNMUTE).replace(':id', encodeURIComponent(String(muteTargetId)));
    setMuteLoading(true);
    try {
      await postApiCall(url, {
        call_id: muteTargetId,
        activeCli: useCallStore.getState().activeCliNumber || null,
        from: useCallStore.getState().callerNumber || null,
        agent_id: userData?.agentData?.id,
        pbx_name: useCallStore.getState().pbxName || null,
      });
      setMuteOn((v) => !v);
    } catch (err: any) {
      toast.error(friendlyApiError(err) || `Failed to ${goingToMute ? 'mute' : 'unmute'} call`);
    } finally { setMuteLoading(false); }
  }, [muteLoading, muteOn, userData]);

  const handleOpenTransfer = useCallback(() => {
    if (callState === 'idle') { toast.error('No active call to transfer.'); return; }
    // The customer being invited/rung (dialing, not yet answered) isn't
    // actually on the line yet — nothing real to transfer until bridge_time
    // exists. Dialer.tsx's button already disables on this, this is the
    // handler-level backstop.
    if (!useCallStore.getState().activeCallMeta?.bridge_time) { toast.error('Wait for the customer to answer before transferring.'); return; }
    // Transfer/Conference place a new outbound leg to the chosen target,
    // same as dialing out — an agent without outbound access could still
    // reach this via the in-call control (it doesn't touch the dial pad),
    // so it needs its own check.
    if (!isOutboundEnabled) { toast.error('Outbound calling is disabled for this agent.'); return; }
    trackActivity('transfer_open', { call_id: useCallStore.getState().sipHeaders?.['call-id'] || null });
    setPickerMode('transfer');
  }, [callState, isOutboundEnabled]);
  const handleOpenConference = useCallback(() => {
    if (callState === 'idle') { toast.error('No active call for conference.'); return; }
    if (!useCallStore.getState().activeCallMeta?.bridge_time) { toast.error('Wait for the customer to answer before adding to conference.'); return; }
    if (!isOutboundEnabled) { toast.error('Outbound calling is disabled for this agent.'); return; }
    trackActivity('conference_open', { call_id: useCallStore.getState().sipHeaders?.['call-id'] || null });
    setPickerMode('conference');
  }, [callState, isOutboundEnabled]);

  const handleAcceptCall = useCallback(async () => {
    const store = useCallStore.getState();
    if (!store.session) { console.error('No session found'); return; }
    if (store.isAnswering) return;
    store.setAnswering(true);
    setManualScreen(null);
    // The actual SIP-level answer, and the WebRTC media-quality monitor
    // (MOS/codec — Call Details' quality fields) it starts on success, only
    // happen because demo-1.ts binds a document-level click listener that
    // independently reacts to the same native click bubbling up from the
    // Dialer's Answer button (id="call") — this React handler never calls
    // either. That's a fragile bridge (anything that stops the click's
    // propagation before it reaches `document` silently breaks both), so
    // call the monitor start directly too — demo-1.ts already guards
    // against a duplicate/double start.
    try { (window as any).__ctiStartCallQualityMonitor?.(); } catch {}
    trackActivity('call_accept', {
      number: store.callerNumber, call_id: store.sipHeaders?.['call-id'] || null,
      direction: store.direction, campaign_id: store.sipCampaignId || null, call_group_id: store.sipCallGroupId || null,
    });
  }, [setManualScreen]);

  const handleRejectCall = useCallback(() => {
    const store = useCallStore.getState();
    if (store.callSetupPending) return;
    if (!store.session) {
      // A mobile-profile incoming call has no SIP session at all — this
      // used to just no-op, leaving no working "Reject" for a mobile call
      // (the button was hidden for mobile entirely for the same reason).
      // There's no dedicated mobile-reject endpoint, so tear the ringing
      // channel down the same way handleEndCall does for an active one.
      if (isMobileProfileActive()) {
        const meta = store.activeCallMeta || {};
        const channelId = store.channelId || null;
        const resolveAgentId = () => userData?.agentData?.id || (() => { try { return JSON.parse(Storage.getItem('user') || '{}')?.agentData?.id ?? null; } catch { return null; } })();
        postApiCall(API_AGENT_MOBILE_HANGUP, {
          channelId, bPartyChannelId: store.bPartyChannelId || channelId || null,
          agent_id: resolveAgentId(), activeCli: store.activeCliNumber || null,
          from: store.callerNumber || meta.callee_number || meta.caller_number || null,
          pbx_name: store.pbxName || null,
        }).catch((err) => console.error('Mobile reject failed', err));
        try { store.markActiveCallDeclinedByAgent?.(); } catch (err) { console.error('Error marking call declined:', err); }
        finally { store.setAnswering(false); }
        setManualScreen(null);
        trackActivity('call_reject', {
          number: store.callerNumber, channel_id: channelId,
          direction: store.direction, campaign_id: store.sipCampaignId || null,
        });
      }
      return;
    }
    // This same handler backs both the incoming "Reject" button and the
    // outgoing "Cancel" button (Dialer.tsx's ringing controls) — they are
    // NOT the same SIP operation. `store.session` is a SIP.js Invitation
    // for an incoming call (reject() sends 486 Busy Here) but an Inviter
    // for an outgoing one (which has no reject() — cancel() is what tears
    // down a call that hasn't been answered yet). Calling reject() on an
    // outgoing session silently no-ops, so clicking "Cancel" left the
    // INVITE running on the wire; if the far end answered anyway before
    // it timed out, the call went live and the UI flipped straight to
    // "Hang up" with no cancellation having actually happened.
    const isOutgoing = store.outgoingRingingActive || store.direction === 'outgoing' || store.direction === 'outbound';
    try {
      if (isOutgoing) store.session.cancel?.();
      else store.session.reject?.();
    } catch (err) { console.error('Error ending call:', err); }
    try { store.markActiveCallDeclinedByAgent?.(); } catch (err) { console.error('Error marking call declined:', err); }
    finally { store.setAnswering(false); }
    setManualScreen(null);
    trackActivity('call_reject', {
      number: store.callerNumber, call_id: store.sipHeaders?.['call-id'] || null,
      direction: store.direction, campaign_id: store.sipCampaignId || null,
    });
  }, [setManualScreen, userData]);

  const handleSendToVoicemail = useCallback(() => {
    const store = useCallStore.getState();
    const isCampaignCall = Boolean(
      store.sipCampaignId || store.activeCallMeta?.campaign_id || store.activeCallMeta?.campaignId
    );
    if (isCampaignCall) return;
    if (voicemailInFlightRef.current) return;

    const vm = store.voicemailMeta || {};
    const vmFrom = vm.from || store.callerNumber || null;
    const vmChannels = resolveVoicemailChannels(store);
    if (!vmChannels.channelId || !vmChannels.agentChannelId) {
      toast.error('Call is still connecting. Try Voicemail again in a moment.');
      return;
    }
    const payload = {
      requestId: `vm-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
      ...vmChannels,
      did: vmFrom,
      agentId: userData?.agentData?.id || null,
      callGroupId: vm.callGroupId || null,
      callSource: vm.callGroupId ? 'QUEUE' : 'STASIS',
      activeCli: store.activeCliNumber || null,
      from: vmFrom,
      pbx_name: store.pbxName || null,
    };

    voicemailInFlightRef.current = true;
    setVoicemailSending(true);
    postApiCall(API_AGENT_VOICEMAIL, payload)
      .then(() => {
        useCallStore.getState().markActiveCallSentToVoicemail?.();
        useCallStore.getState().markActiveCallDeclinedByAgent?.();
        useCallStore.getState().endCall?.({ resetIdle: false });
        setManualScreen(null);
        trackActivity('call_voicemail', {
          number: store.callerNumber, call_id: store.sipHeaders?.['call-id'] || null,
          direction: store.direction, call_group_id: vm.callGroupId || null,
        });
      })
      .catch((err: any) => {
        toast.error(friendlyApiError(err) || 'Could not send call to voicemail. Please try again.');
      })
      .finally(() => {
        voicemailInFlightRef.current = false;
        setVoicemailSending(false);
      });
  }, [setManualScreen, userData]);

  const handleEndCall = useCallback(() => {
    const store = useCallStore.getState();
    if (store.callSetupPending) return;
    store.requestLocalHangup();
    if (!store.session) {
      if (isMobileProfileActive()) {
        const meta = store.activeCallMeta || {};
        const channelId = store.channelId || null;
        const resolveAgentId = () => userData?.agentData?.id || (() => { try { return JSON.parse(Storage.getItem('user') || '{}')?.agentData?.id ?? null; } catch { return null; } })();
        store.setAnswering(false);
        trackActivity('call_end', { number: store.callerNumber || meta.callee_number || meta.caller_number || null, channel_id: channelId, direction: store.direction, start_time: meta.start_time || null });
        postApiCall(API_AGENT_MOBILE_HANGUP, {
          channelId, bPartyChannelId: store.bPartyChannelId || channelId || null,
          agent_id: resolveAgentId(), activeCli: store.activeCliNumber || null,
          from: store.callerNumber || meta.callee_number || meta.caller_number || null,
          pbx_name: store.pbxName || null,
        }).catch((err) => console.error('Mobile hangup failed', err));
      }
      return;
    }
    store.setAnswering(false);
    const meta = store.activeCallMeta || {};
    trackActivity('call_end', {
      number: store.callerNumber || meta.callee_number || meta.caller_number || null,
      call_id: store.sipHeaders?.['call-id'] || null, direction: store.direction,
      campaign_id: store.sipCampaignId || null, start_time: meta.start_time || null,
    });
  }, [userData]);

  return {
    holdOn, muteOn, holdLoading, muteLoading,
    pickerMode, setPickerMode,
    handleToggleHold, handleToggleMute, handleOpenTransfer, handleOpenConference,
    handleAcceptCall, handleRejectCall, handleEndCall,
    handleSendToVoicemail, voicemailSending,
  };
}
